import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination } from "antd";
import { useParams } from "react-router-dom";

import APIService from "../../Service/APIService";
import Table from "../../common/table";
import "./ManufacturerInventoryTab.css";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_STATE = {
  field: "createdAt",
  order: "desc",
};

const formatSignedQuantity = (value) => {
  const quantity = Number(value || 0);
  return quantity > 0 ? `+${quantity}` : `${quantity}`;
};

const ManufacturerInventoryTab = () => {
  const { id } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [sortState, setSortState] = useState(DEFAULT_SORT_STATE);

  const columnDefs = useMemo(() => [
    {
      headerName: "Date",
      field: "createdAt",
      sortable: true,
      minWidth: 125,
      valueGetter: (params) => params.data?.Date || "",
    },
    {
      headerName: "Product Code",
      field: "ProductCode",
      sortable: true,
      minWidth: 180,
      flex: 1,
    },
    {
      headerName: "Qty Change",
      field: "DeltaQty",
      sortable: true,
      minWidth: 150,
      cellRenderer: (params) => formatSignedQuantity(params.value),
    },
    {
      headerName: "Updated Qty",
      field: "NewStockQty",
      sortable: true,
      minWidth: 155,
      cellRenderer: (params) => Number(params.value || 0),
    },
    {
      headerName: "Order/Stock",
      field: "OrderOrStockLabel",
      sortable: false,
      minWidth: 220,
      flex: 1,
    },
  ], []);

  const fetchInventory = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const apiServe = new APIService();
      const params = new URLSearchParams({
        ID: id,
        page: String(page),
        pageSize: String(pageSize),
        sortField: sortState?.field || DEFAULT_SORT_STATE.field,
        sortOrder: sortState?.order || DEFAULT_SORT_STATE.order,
      });
      const response = await apiServe.getDataFromBackend(
        `getmanufacturerinventory?${params.toString()}`
      );
      const payload = response?.data || {};
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotalCount(Number(payload.totalCount || 0));
    } catch (err) {
      setRows([]);
      setTotalCount(0);
      setError(
        err?.response?.data?.error ||
          "Unable to load inventory history."
      );
    } finally {
      setLoading(false);
    }
  }, [id, page, pageSize, sortState]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handlePaginationChange = (nextPage, nextPageSize) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  const handleSortChange = (nextSortState) => {
    setSortState(nextSortState || DEFAULT_SORT_STATE);
    setPage(DEFAULT_PAGE);
  };

  return (
    <div className="manufacturer-inventory-tab">
      <h2 className="manufacturer-inventory-tab__header">
        Stock Change Log
      </h2>

      <div className="manufacturer-inventory-tab__table">
        <Table
          rowData={rows}
          columnDefs={columnDefs}
          pagination={false}
          loading={loading}
          serverSideSort
          sortState={sortState}
          onSortChange={handleSortChange}
          screen="ManufacturerStockLog"
        />
        {!loading && error && (
          <div className="manufacturer-inventory-tab__state manufacturer-inventory-tab__state--error">
            {error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="manufacturer-inventory-tab__state">
            No stock changes found.
          </div>
        )}
      </div>

      {!loading && !error && totalCount > 0 && (
        <div className="manufacturer-inventory-tab__pagination">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={["20", "50", "100"]}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total}`
            }
          />
        </div>
      )}
    </div>
  );
};

export default ManufacturerInventoryTab;
